import React, { ElementType, forwardRef, ReactElement } from "react";
import classNames from "classnames";
import Tokens from "@ramsey-design-system/tokens/dist/main.json";
import { ButtonProps, ComponentProps, ButtonComponent } from "./types";
import { GenericRef } from "@ramsey-design-system/common/types";

const getConditionalProps = (
  disabled: ComponentProps["disabled"],
  href: ComponentProps["href"],
  type: ComponentProps["type"]
) => ({
  "aria-disabled": (href && disabled) || null,
  disabled: !href && disabled,
  type: (!href && type) || null,
  href: (!disabled && href) || null,
});

const getIconProps = (
  iconOnly: ComponentProps["iconOnly"],
  iconPosition: ComponentProps["iconPosition"],
  buttonSize: ComponentProps["size"]
) => {
  const iconClasses = classNames("rds-Button-icon", {
    [`rds-Button-icon--${iconPosition}`]: !iconOnly,
  });

  let iconSize = buttonSize === "small" ? Tokens.SizeIconSm : Tokens.SizeIconMd;
  if (iconOnly && (buttonSize === "large" || buttonSize === "xlarge")) {
    iconSize = Tokens.SizeIconLg;
  }

  return {
    fill: "currentColor",
    className: iconClasses,
    height: iconSize,
    width: iconSize,
    ["aria-hidden"]: true,
    focusable: false,
  };
};

const validateAccessibility = (
  iconOnly: ComponentProps["iconOnly"],
  rest: ComponentProps
) => {
  const ariaLabel = rest["aria-label"];
  if (iconOnly && !ariaLabel) {
    // eslint-disable-next-line no-console
    console.error(
      "If you are adding an 'iconOnly' Button, please provide an accessible 'aria-label' attribute. See the Button Accessibility docs for more information: https://design.ramseysolutions.com/components/button/#accessibility"
    );
  }
};

const getElement = (
  href: ComponentProps["href"],
  element: ElementType | undefined
) => {
  return element || (href ? "a" : "button");
};

function Button<C extends ElementType>(
  {
    appearance = "primary",
    children,
    className,
    disabled = false,
    element,
    href,
    fullWidth = false,
    fullWidthOnMobile = false,
    icon: ButtonIcon,
    iconOnly = false,
    iconPosition = "left",
    isOnDarkCanvas = false,
    size = "medium",
    type = "button",
    ...rest
  }: ButtonProps<C>,
  ref: GenericRef<C>
): ReactElement {
  const Element = getElement(href, element);
  const buttonClasses = classNames(
    "rds-Button",
    {
      [`rds-Button--${appearance}`]: appearance !== "primary",
      [`rds-Button--${size}`]: size !== "medium",
      "rds-Button--fullWidth": !iconOnly && fullWidth,
      "rds-Button--fullWidthOnMobile": !iconOnly && fullWidthOnMobile,
      "rds-Button--iconOnly": iconOnly,
      "is-disabled": href && disabled,
      "on-darkCanvas": isOnDarkCanvas,
    },
    className
  );

  // warn if iconOnly is 'true' without aria-label attribute
  validateAccessibility(iconOnly, rest);

  return (
    <Element
      className={buttonClasses}
      {...getConditionalProps(disabled, href, type)}
      {...rest}
      ref={ref}
    >
      {ButtonIcon && iconPosition === "left" && (
        <ButtonIcon {...getIconProps(iconOnly, iconPosition, size)} />
      )}
      {!iconOnly && children}
      {ButtonIcon && iconPosition === "right" && (
        <ButtonIcon {...getIconProps(iconOnly, iconPosition, size)} />
      )}
    </Element>
  );
}

const Component = forwardRef(Button);
Button.displayName = "Button";

export default Component as ButtonComponent;
